import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ampli } from '../../ampli';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { StoryCard } from '../StoryCard/StoryCard';
import { ReactComponent as ListIcon } from '../../assets/list.svg';
import { ReactComponent as MessageIcon } from '../../pages/NarrativePage/assets/messages.svg';
import { ReactComponent as ArrowIcon } from '../../pages/NarrativePage/assets/arrow.svg';
import { ReactComponent as ArrowDownIcon } from '../../pages/NarrativePage/assets/arrowDown.svg';
import { ReactComponent as SortIcon } from '../../assets/sort.svg';
import { ReactComponent as MergeIcon } from '../../pages/NarrativePage/assets/merge.svg';
import { ReactComponent as DeleteIcon } from '../../assets/delete.svg';
import {ReactComponent as SearchIcon} from '../../assets/search.svg';
import { ReactComponent as AddToIcon} from '../../assets/addTo.svg';
import { ReactComponent as AnchorIcon } from '../../assets/anchor.svg';
import { API } from '../../API';
import { LoaderSmall } from '../LoaderSmall/LoaderSmall';
import Modal from '../Modal';
import { Search } from '../Search/Search';

import { Calendar } from '../../pages/SearchPage/Calendar/Calendar';
import DatePickerWrapper from '../Datepicker/DatepickerWrapper';
import { DropdownMenu } from '../DropdownMenu/DropdownMenu';
import { Radiobutton } from '../DropdownMenu/Radiobutton';
import { SortingOrder } from '../DropdownMenu/SortingOrder';
import { Filter } from './Filter/Filter';
import { AddToDashboardModal } from '../AddToDashboardModal/AddToDashboardModal';

import styles from './StoriesFeed.module.scss';
import { SkeletonStoryCard } from '../StoryCard/SkeletonStoryCard';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { showRequestError } from '../../utils/requestError';


export const StoriesFeed = ({
  report,
  stories,
  opinions = [],
  currentStory,
  onTabChange,
  onFilterChange,
  onStoryChange,
  onOpinionChange,
  onDateChange,
  onSortingChange,
  sourceGroups,
  countries,
  countryGroups,
  sentiment,
  startDate,
  endDate,
  sorting,
  searchQuery,
  page,
  onStoryEdit,
  onStoryDelete,
  hasMoreEvents,
  fetchMoreEvents,
  onSearch,
  isAttitude=false,
  isShare=false,
}) => {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const [activeTab, setActiveTab] = useState('stories');
  const [activeStory, setActiveStory] = useState();
  const [activeOpinion, setActiveOpinion] = useState(opinions[0]);
  const [dropdownMenu, setDropdownMenu] = useState(false);
  const [read, setRead] = useState({});
  const [selectedEvents,  setSelectedEvents] = useState([]);
  const [mergeEventsModal, setMergeEventsModal] = useState({
    isActive: false,
    event_ids: [],
    new_summary: '',
    new_title: '',
    isLoading: false,
    error: null,
  });

  const [deleteEventsModal, setDeleteEventsModal] = useState({
    isActive: false,
    event_ids: [],
  });

  const [addToTopicModal, setAddToTopicModal] = useState({
    isActive: false,
    event_ids: [],
    report_id: null,
    error: null,
  });

  const [showSearch, setShowSearch] = useState(false);

  // Virtualizer setup
  const scrollContainerRef = useRef(null);

  const virtualizer = useVirtualizer({
    count: stories?.length || 0,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 150,
    overscan: 10,
    measureElement: (element) => element?.getBoundingClientRect().height || 150,
  });

  const virtualItems = virtualizer.getVirtualItems();

  // Scroll handler for infinite loading
  const isFetchingRef = useRef(false);

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || !hasMoreEvents || isFetchingRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      isFetchingRef.current = true;
      Promise.resolve(fetchMoreEvents()).then(() => {
        isFetchingRef.current = false;
      }).catch(() => {
        isFetchingRef.current = false;
      });
    }
  }, [hasMoreEvents, fetchMoreEvents]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if(showSearch === false && searchQuery?.trim().length > 0) {
      onSearch('');
    }
  }, [showSearch]);
  
  useEffect(() => {
      setActiveStory(currentStory);
      if (currentStory?.is_read === false && !read[currentStory?.id]) {
        setRead({...read, [currentStory.id]: true});
      }
    }, [currentStory]);

  // useEffect(() => {
  //   setActiveStory(stories[0]);
  //   setActiveOpinion(opinions[0]);
  //   onTabChange(activeTab);
  // }, [activeTab]);

  const handleDeleteEvents = (e, events) => {
    e.stopPropagation();
    e.preventDefault();
    API.fetch('DELETE', `/API/v1/topic-modeling/${report.id}/events`, null, {event_ids: events}).then(data => {
      setDeleteEventsModal({
        isActive: false,
        event_ids: [],
      });
      setSelectedEvents([]);
      onStoryDelete();
    }).catch((e) => showRequestError(e, t('Delete events')));
  }

  const initStartDate = null;
  const initEndDate = null;

  const sortingMenu = [
    {
      name: t('Highest rating'),
      value: 'rating',
    },
    {
      name: t('Most viewed'),
      value: 'views',
    },
    {
      name: t('Highest engagement'),
      value: 'engagement',
    },
    {
      name: t('Most High-Risk accounts'),
      value: 'high_risk_accounts_count',
    },
    {
      name: t('Most recent'),
      value: 'recency'
    },
    {
      name: t('Most publications'),
      value: 'messages_count',
    },
  ];

  if (mergeEventsModal.isActive) {
    const textarea = document.getElementById('new-story-summary');

    textarea?.addEventListener('input', function () {
      this.style.height = 'auto';
      this.style.height = this.scrollHeight + 'px';
    });
  }

  return (
    <div className={styles.mainContainer}>
      <div className={styles.filtersWrapper}>
        <div className={styles.filtersContainer}>
          <div className={styles.calendarWrapper} translate='no'>
            <DatePickerWrapper
              onChange={(startDate, endDate) => {
               onDateChange(startDate, endDate);

               ampli.track({
                  event_type: 'Change date range in topic report',
                  event_properties: {
                    user_id: currentUser?.id,
                    workspace_id: currentUser?.workspace_id,
                    report_id: report?.id,
                    new_date_range: `${startDate} - ${endDate}`,
                  },
                });
              }}
              initStartDate={startDate}
              initEndDate={endDate}
              isRange={true}
              buttonDateFormat = 'dd/MM/yy'
            />
            <div
              className={styles.hintIcon}
              data-tooltip-id="calendar-tooltip"
              data-tooltip-place="bottom-center" 
            >
              <AnchorIcon />
              <ReactTooltip id="calendar-tooltip" style={{ width: '365px'}} data-tooltip-position-strategy='absolute' >
                <div className={styles.tooltipContent}>
                  <p>{t('Filter events based on when they were reported.')}</p>
                  <p>{t('If an event has coverage both inside and outside the date range, only the posts from the selected period are included. This may affect statistics.')}</p>
                </div>
              </ReactTooltip>
            </div>
          </div>
          <div className={styles.filters}>
            <button
              className={showSearch ? styles.searchButtonActive : styles.searchButton}
              onClick={() => setShowSearch(!showSearch)}
            >
              <SearchIcon/>
            </button>
            <DropdownMenu
              isOpen={dropdownMenu}
              header={t('Sort by')}
              onClick={() => setDropdownMenu(!dropdownMenu)}
              // buttonName={t('Sort')}
              icon={<SortIcon />}
              menuClassName="only-icon"
            >
              <Radiobutton
                itemsList={sortingMenu}
                key={sorting.fieldName}
                current={sorting.fieldName}
                onChange={(value) => {
                  onSortingChange({ ...sorting, fieldName: value })
                  ampli.track({
                    event_type: 'Change sorting in topic report',
                    event_properties: {
                      user_id: currentUser?.id,
                      workspace_id: currentUser?.workspace_id,
                      report_id: report?.id,
                      new_sorting: value,
                    },
                  });
                }}
              />
            </DropdownMenu>

            <Filter
              onlyIcon={true}
              isShare={isShare}
              className="onlyIcon"
              sourceGroupsFilter={sourceGroups}
              countriesFilter={countries}
              countryGroupsFilter={countryGroups}
              sentimentFilter={sentiment}
              onChange={(sourceGroups, countries, sentiment, countryGroups) => {
                onFilterChange(sourceGroups, countries, sentiment, countryGroups);
                let groups = sourceGroups.map((group) => group.label);
                let countriesList = countries.map((country) => country.label);
                ampli.track({
                  event_type: 'Change filters in topic report',
                  event_properties: {
                    user_id: currentUser?.id,
                    workspace_id: currentUser?.workspace_id,
                    report_id: report?.id,
                    source_groups: groups,
                    origin_countries: countriesList,
                    country_groups: countryGroups,
                    sentiment: sentiment,
                  },
                });
              }}
            />
          </div>
        </div>
        {showSearch ? <div className={styles.searchWrapper}>
          <Search
            className={styles.eventsSearch}
            placeholder={t('Search')}
            onChange={(value) => {
              onSearch(value);
              ampli.track({
                event_type: 'Search Perform in topic report',
                event_properties: {
                  user_id: currentUser?.id,
                  workspace_id: currentUser?.workspace_id,
                  report_id: report?.id,
                },
              });
            }}
          />
        </div> : ''}
      </div>
      <div className={styles.feedMainContainer}>
        <div className={styles.controls}>
          <div
            className={
              activeTab === 'stories'
                ? `${styles.tab} ${styles.active}`
                : styles.tab
            }
            onClick={() => setActiveTab('stories')}
          >
            <ListIcon />
            {t('Topics')}
          </div>
          {/* <div
            className={activeTab === 'opinions' ? `${styles.tab} ${styles.active}` : styles.tab}
            onClick={() => setActiveTab('opinions')}
          >
            <MessageIcon />
            {t('Opinions')}
          </div> */}
          {currentUser?.is_super_admin && selectedEvents?.length > 0 ? (
            <>
              <button
                type="button"
                className={styles.deleteButton}
                aria-label={t('Delete')}
                data-tooltip-id="stories-actions-tooltip"
                data-tooltip-content={t('Delete')}
                onClick={() =>
                  setDeleteEventsModal({
                    isActive: true,
                    event_ids: selectedEvents,
                  })
                }
              >
                <DeleteIcon />
              </button>
              <button
                type="button"
                className={styles.mergeButton}
                aria-label={t('Merge')}
                data-tooltip-id="stories-actions-tooltip"
                data-tooltip-content={t('Merge')}
                onClick={() =>
                  setMergeEventsModal({
                    isActive: true,
                    event_ids: selectedEvents,
                    new_summary:
                      stories
                        ?.filter((event) => selectedEvents.includes(event.id))
                        .sort((a, b) => b.rating - a.rating)?.[0]
                        ?.description || '',
                    new_title:
                      stories
                        ?.filter((event) => selectedEvents.includes(event.id))
                        .sort((a, b) => b.rating - a.rating)?.[0]?.title ||
                      '',
                    isLoading: false,
                    error: null,
                  })
                }
              >
                <MergeIcon />
              </button>
              <button
                type="button"
                className={styles.copyButton}
                aria-label={t('Copy to dashboard')}
                data-tooltip-id="stories-actions-tooltip"
                data-tooltip-content={t('Copy to dashboard')}
                onClick={() =>
                  setAddToTopicModal({
                    isActive: true,
                    event_ids: selectedEvents,
                    dashboard_id: null,
                    report_id: report.id,
                    error: null,
                  })
                }
              >
                <AddToIcon />
              </button>
              {/* The stories panel is position: sticky and forms its own
                  stacking context, so a tooltip rendered inside it is painted
                  under the right-hand content. Portal it to <body>. */}
              {createPortal(
                <ReactTooltip
                  id="stories-actions-tooltip"
                  place="bottom"
                  positionStrategy="fixed"
                  style={{ zIndex: 20 }}
                />,
                document.body,
              )}
            </>
          ) : (
            ''
          )}
        </div>

        {activeTab === 'stories' ? (
          stories ? (
            stories.length > 0 ? (
              <div ref={scrollContainerRef} className={styles.feedContainer} id="scrollableDiv">
                <div style={{ height: `${virtualizer.getTotalSize() + 60}px`, width: '100%', position: 'relative' }}>
                  {virtualItems.map((virtualRow) => {
                    const story = stories[virtualRow.index];
                    return (
                      <div
                        key={story?.id}
                        data-index={virtualRow.index}
                        ref={virtualizer.measureElement}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        <StoryCard
                          story={story}
                          isShare={isShare}
                          report={report}
                          isActive={activeStory?.id === story?.id}
                          isRead={read[story?.id] || story?.is_read}
                          onChange={(story) => onStoryChange(story)}
                          isAdmin={currentUser?.is_super_admin}
                          onEdit={(story) => onStoryEdit(story)}
                          onDelete={() => onStoryDelete()}
                          onCheckboxClick={(story) => {
                            if (selectedEvents.includes(story.id)) {
                              setSelectedEvents(
                                selectedEvents.filter(
                                  (event) => event !== story.id,
                                ),
                              );
                            } else {
                              setSelectedEvents([...selectedEvents, story.id]);
                            }
                          }}
                          selectedEvents={selectedEvents}
                          viewMode={report?.widget_config?.event_card || 'rating'}
                          isAttitude={isAttitude}
                        />
                      </div>
                    );
                  })}

                  {hasMoreEvents && (
                    <div
                      className={styles.feedLoader}
                      style={{
                        position: 'absolute',
                        top: `${virtualizer.getTotalSize()}px`,
                        left: 0,
                        width: '100%',
                      }}
                    >
                      <LoaderSmall />
                    </div>
                  )}
                  {!hasMoreEvents && stories?.length > 0 && (
                    <div
                      className={styles.feedLoader}
                      style={{
                        position: 'absolute',
                        top: `${virtualizer.getTotalSize()}px`,
                        left: 0,
                        width: '100%',
                      }}
                    >
                      {t('Yay! You have seen it all')}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className={styles.feedContainer}>
                <div className={styles.noData}>
                  {report?.content_analyzed === null ? t('Please, wait till our AI build events for this case. It takes up to 3 hours on average.') : searchQuery?.trim().length > 0 ||
                  sourceGroups?.length > 0 ||
                  countries?.length > 0 ||
                  countryGroups?.length > 0 ||
                  sentiment?.length > 0 ||
                  startDate !== new Date(report.start_date) ||
                  endDate !== new Date(report.end_date)
                    ? t(
                        'No results were found for this query, refine your search',
                      )
                    : t('There is no data to display')}
                </div>
              </div>
            )
          ) : (
            <div className={styles.feedContainer}>
              {[...Array(3).keys()].map((story, index) => (
                <SkeletonStoryCard key={`${story.id}_${index}`} />
              ))}
            </div>
          )
        ) : (
          ''
        )}
        {activeTab === 'opinions' ? (
          opinions ? (
            opinions.length > 0 ? (
              <div className={styles.feedContainer}>
                {opinions?.map((opinion) => (
                  <StoryCard
                    key={opinion?.id}
                    story={opinion}
                    report={report}
                    isActive={activeOpinion?.id === opinion?.id}
                    onChange={(opinion) => setActiveOpinion(opinion)}
                    isStory={false}
                    isShare={isShare}
                  />
                ))}
              </div>
            ) : (
              <div className={styles.feedContainer}>
                <div className={styles.noData}>
                  {searchQuery?.trim().length > 0
                    ? t(
                        'No results were found for this query, refine your search',
                      )
                    : t('There is no data to display')}
                </div>
              </div>
            )
          ) : (
            <div className={styles.feedContainer}>
              {[...Array(3).keys()].map((story, index) => (
                <SkeletonStoryCard key={`${story.id}_${index}`} />
              ))}
            </div>
          )
        ) : (
          ''
        )}
      </div>

      {mergeEventsModal.isActive ? (
        <Modal
          isVisible={mergeEventsModal.isActive}
          className={styles.mergeEventsModal}
          title={t('Merge stories')}
          content={
            mergeEventsModal.event_ids.length < 2 ? (
              <p className={styles.errorStoriesMerge}>
                {t('Please select at least 2 events to merge')}
              </p>
            ) : (
              <div className={styles.mergeStoriesModal}>
                {mergeEventsModal.error ? (
                  <p className={styles.errorStoriesMerge}>
                    {mergeEventsModal.error}
                  </p>
                ) : (
                  ''
                )}
                <div className="form-element">
                  <label htmlFor="new-story-title">{t('Title')}&#42;</label>
                  <input
                    type="text"
                    id="new-story-title"
                    value={mergeEventsModal.new_title}
                    required
                    onChange={(e) =>
                      setMergeEventsModal({
                        ...mergeEventsModal,
                        new_title: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="form-element">
                  <label htmlFor="new-story-summary">
                    {t('Description')}&#42;
                  </label>
                  <textarea
                    id="new-story-summary"
                    value={mergeEventsModal.new_summary}
                    required
                    onChange={(e) =>
                      setMergeEventsModal({
                        ...mergeEventsModal,
                        new_summary: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            )
          }
          footer={
            <>
              {mergeEventsModal.event_ids.length < 2 ? (
                ''
              ) : (
                <button
                  className="btn-primary"
                  disabled={mergeEventsModal.isLoading}
                  onClick={() => {
                    setMergeEventsModal({
                      ...mergeEventsModal,
                      isLoading: true,
                    });

                    API.fetch(
                      'POST',
                      `/API/v1/topic-modeling/${report.id}/events/merge`,
                      null,
                      {
                        event_ids: mergeEventsModal.event_ids,
                        new_description: mergeEventsModal.new_summary,
                        new_title: mergeEventsModal.new_title,
                      },
                    )
                      .then(() => {
                        setMergeEventsModal({
                          isActive: false,
                          event_ids: [],
                          new_summary: '',
                          new_title: '',
                          isLoading: false,
                          error: null,
                        });

                        setSelectedEvents([]);

                        onStoryDelete();
                      })
                      .catch((e) => {
                        setMergeEventsModal({
                          ...mergeEventsModal,
                          isLoading: false,
                          error: t(
                            'Something went wrong. Please, try again...',
                          ),
                        });
                        showRequestError(e, t('Merge events'));
                      });
                  }}
                >
                  {mergeEventsModal.isLoading ? <LoaderSmall /> : t('Merge')}
                </button>
              )}
              <button
                className="btn-reset"
                disabled={mergeEventsModal.isLoading}
                onClick={() =>
                  setMergeEventsModal({
                    isActive: false,
                    event_ids: [],
                    new_summary: '',
                    new_title: '',
                    isLoading: false,
                    error: null,
                  })
                }
              >
                {t('Cancel')}
              </button>
            </>
          }
          onClose={() =>
            setMergeEventsModal({
              isActive: false,
              event_ids: [],
              new_summary: '',
              new_title: '',
              isLoading: false,
              error: null,
            })
          }
        />
      ) : (
        ''
      )}
      {deleteEventsModal.isActive ? (
        <Modal
          isVisible={deleteEventsModal.isActive}
          title={t('Delete')}
          content={<p>{t('Are you sure you want to delete this events?')}</p>}
          footer={
            <>
              <button
                className="btn-danger"
                onClick={(e) => {
                  handleDeleteEvents(e, deleteEventsModal.event_ids);
                }}
              >
                {t('Delete')}
              </button>
              <button
                className="btn-reset"
                onClick={() => {
                  setDeleteEventsModal({
                    isActive: false,
                    event_ids: [],
                  });
                }}
              >
                {t('Cancel')}
              </button>
            </>
          }
          onClose={() => setDeleteEventsModal({ isActive: false, event_ids: [] })}
        />
      ) : (
        ''
      )}
      {addToTopicModal.isActive ? (
        <AddToDashboardModal
          isOpen={addToTopicModal.isActive}
          eventIds={addToTopicModal.event_ids}
          reportId={addToTopicModal.report_id}
          onClose={() => {
            setAddToTopicModal({ isActive: false, event_ids: [], report_id: null });
          }}
          onChange={() => {
            setAddToTopicModal({ isActive: false, event_ids: [], report_id: null });
            setSelectedEvents([]);
          }}
        />
      ) : (
        ''
      )}
    </div>
  );
};
